// Серверный слой песочницы кастомных HTML-страниц (docs/custom-html-sdk.md).
// В отличие от useCubus.* эти обёртки не тостят ошибки, а бросают их с кодом —
// хост возвращает их странице как res.error по протоколу моста.
import useJwt from '@/cubus/jwt/useJwt'
import Q from '@/cubus/services/Q'

export function bridgeError(code, message, details) {
  const e = new Error(message)
  e.code = code
  e.details = details === undefined ? null : details
  return e
}

function token() {
  return useJwt.getCubusToken()
}

function runQuery(query) {
  return useJwt.query({ token: token(), query })
    .then(response => {
      if (response.data && response.data.error) {
        throw bridgeError('E_QUERY', String(response.data.error), response.data)
      }
      if (!response.data || !response.data.result) {
        throw bridgeError('E_QUERY', 'empty backend response')
      }
      return response.data.result
    }, err => {
      if (err && err.code) throw err
      throw bridgeError('E_INTERNAL', String(err))
    })
}

// Плоский фильтр страницы → штатное дерево условий Cubus.
// { attr: value } | { attr: { op: 'ge', value: 100 } }, op из OPS.
const OPS = {
  eq: 'equal',
  ne: 'notequal',
  gt: 'greater',
  ge: 'notless',
  lt: 'less',
  le: 'notgreater',
  like: 'like',
  ilike: 'ilike',
  in: 'in',
}

export function buildFilter(flat) {
  if (!flat) return null
  if (flat.node) return flat // уже полное дерево — экскейп-хатч
  const conds = []
  Object.keys(flat).forEach(field => {
    const v = flat[field]
    if (v !== null && typeof v === 'object' && v.op !== undefined) {
      const oper = OPS[v.op]
      if (!oper) throw bridgeError('E_PROTOCOL', `unknown filter op "${v.op}"`)
      if (v.value === null || v.value === undefined) {
        conds.push(Q.cond(v.op === 'ne' ? 'isnotnull' : 'isnull', field))
      } else {
        conds.push(Q.cond(oper, field, v.value))
      }
    } else {
      conds.push(Q.equals(field, v))
    }
  })
  if (conds.length === 0) return null
  return Q.and(conds)
}

// BUG-151: обе сборки принимали ТОЛЬКО короткую форму моста (массив), а всё остальное —
// в том числе штатное дерево Cubus (`{groups:[…]}` / `{sorts:[…]}`, ровно то, что автор
// страницы видит в MCP и в конфигах представлений) — отсекалось первой строкой и уезжало
// на бэк как null. Для groupby это худший вид отказа: ошибки нет, recordcount тот же,
// вместо 196 групп страница получает миллион сырых строк и выглядит рабочей. У фильтра
// escape-hatch на штатное дерево был с самого начала (`flat.node`), здесь его не было.
// Теперь принимаются обе формы, а нераспознанная отвергается кодом, а не тишиной.
const GROUPBY_FORMS = '"attr" | ["attr","oper"] | {oper:{sid},entity:{sid|value}}'
const ORDERBY_FORMS = '"attr" | ["attr","asc"|"desc"] | {oper:{sid},entity:{sid|value}}'

// Штатный item бэка обязан нести oper.sid: и Groupby, и Orderby читают его через
// `.as[String]`, поэтому item без oper отвечает сырым JsResultException — отвечаем сами.
function nativeItem(item, kind, forms) {
  if (!item.entity || typeof item.entity !== 'object') {
    throw bridgeError('E_PROTOCOL', `${kind} item has no "entity" — expected ${forms}`)
  }
  if (!item.oper || !item.oper.sid) {
    throw bridgeError('E_PROTOCOL', `${kind} item for entity ${JSON.stringify(item.entity)} has no "oper.sid"`)
  }
  return item
}

function groupbyItem(g) {
  // Строка без операнда — это `sum`, как в Q.groupBy: ключ группировки задаётся парой
  // ['attr','group'], иначе поле просуммируется вместо того, чтобы стать разрезом.
  if (typeof g === 'string') {
    return {
      isActive: true, name: null, oper: { sid: 'sum' }, entity: { sid: g },
    }
  }
  if (Array.isArray(g)) {
    const [field, op] = g
    if (typeof field !== 'string' || !field) {
      throw bridgeError('E_PROTOCOL', `groupby item ${JSON.stringify(g)} must start with an attribute sid`)
    }
    return {
      isActive: true, name: null, oper: { sid: String(op || 'sum').toLowerCase() }, entity: { sid: field },
    }
  }
  if (g && typeof g === 'object') return nativeItem(g, 'groupby', GROUPBY_FORMS)
  throw bridgeError('E_PROTOCOL', `invalid groupby item ${JSON.stringify(g)} — expected ${GROUPBY_FORMS}`)
}

function orderbyItem(s) {
  if (typeof s === 'string') return { isActive: true, oper: { sid: 'asc' }, entity: { sid: s } }
  if (Array.isArray(s)) {
    const [field, dir] = s
    if (typeof field !== 'string' || !field) {
      throw bridgeError('E_PROTOCOL', `orderby item ${JSON.stringify(s)} must start with an attribute sid`)
    }
    return { isActive: true, oper: { sid: String(dir || 'asc').toLowerCase() }, entity: { sid: field } }
  }
  if (s && typeof s === 'object') return nativeItem(s, 'orderby', ORDERBY_FORMS)
  throw bridgeError('E_PROTOCOL', `invalid orderby item ${JSON.stringify(s)} — expected ${ORDERBY_FORMS}`)
}

// Общая часть: пустое — это «не задано» (null), массив — короткая форма моста,
// объект со списком items — штатное дерево (пропускаем как есть, нормализуя items),
// что угодно ещё — отказ с кодом.
function buildQueryPart(spec, key, itemOf, kind, forms) {
  if (spec === null || spec === undefined || spec === false || spec === '') return null
  if (Array.isArray(spec)) {
    if (spec.length === 0) return null
    return { isActive: true, [key]: spec.map(itemOf) }
  }
  if (typeof spec === 'object' && Array.isArray(spec[key])) {
    if (spec[key].length === 0) return null
    return { ...spec, [key]: spec[key].map(itemOf) }
  }
  throw bridgeError(
    'E_PROTOCOL',
    `${kind} must be an array of ${forms}, or a Cubus ${kind} tree {${key}:[…]} — got ${JSON.stringify(spec)}`,
  )
}

export function buildOrderBy(spec) {
  return buildQueryPart(spec, 'sorts', orderbyItem, 'orderby', ORDERBY_FORMS)
}

export function buildGroupBy(spec) {
  return buildQueryPart(spec, 'groups', groupbyItem, 'groupby', GROUPBY_FORMS)
}

// Запись страницы { attr: 5 } → формат Cubus { attr: { value: 5, status: 'changed' } };
// status ставим как штатные формы (hbtAddLog и др.), иначе saverecord может
// проигнорировать поле. Уже обёрнутые значения не трогаем.
export function normalizeRecord(record) {
  if (!record || typeof record !== 'object') {
    throw bridgeError('E_PROTOCOL', 'record must be an object')
  }
  const out = {}
  Object.keys(record).forEach(key => {
    const v = record[key]
    if (v !== null && typeof v === 'object' && ('value' in v)) out[key] = v
    else out[key] = { value: v, title: null, status: 'changed' }
  })
  return out
}

function rawValue(v) {
  if (v !== null && typeof v === 'object' && ('value' in v)) return v.value
  return v
}

// BUG-042: `id` — единственное, что отличает update от insert (бэк: id 0/пусто →
// insert, иначе update by id). Страница, у которой поиск записи не удался, легко
// отдаёт `Number(null)` = 0 или NaN — и вместо правки молча появлялся дубль
// (тоггл галочки давал две строки за день). Пустой id пропускаем (это insert),
// но НЕнулевой мусор отвергаем с внятным текстом вместо тихой вставки.
// Числовую строку принимаем: без этого бэк отвечал сырым
// «JsResultException ... error.expected.jsnumber».
function normalizeRecordId(raw) {
  const v = rawValue(raw)
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v).trim())
  if (!Number.isInteger(n) || n <= 0) {
    throw bridgeError(
      'E_PROTOCOL',
      `record id must be a positive integer, got ${JSON.stringify(v)} — omit "id" to insert a new record`,
    )
  }
  return n
}

// PROP-046: мост отдавал мягко удалённые строки, а MCP на том же движке — нет, потому что
// движок прячет их только у токенов БЕЗ права `marked`. Автор страницы был обязан помнить об
// этом и фильтровать сам, иначе снятая (удалённая) отметка продолжала считаться выполненной —
// ровно этот симптом и наблюдался у пользователя. Теперь дефолт один: не показывать. Условие
// ставит бэк (`hidedeleted`), а не страница, потому что только он знает, есть ли у объекта
// поле `del` — у представлений его нет, и лишнее условие уронило бы запрос.
export function queryObject(params) {
  return runQuery({
    method: 'query',
    mode: 'sync',
    param: {
      keymode: 'sid',
      objectid: null,
      objectsid: params.objectsid,
      domainsid: null,
      hidedeleted: !(params.include_deleted || params.includeDeleted),
      filter: buildFilter(params.filter),
      // camelCase — такой же алиас, как pageSize/includeDeleted ниже: страница, написавшая
      // groupBy, не должна молча получить неагрегированный ответ (BUG-151).
      groupby: buildGroupBy(params.groupby !== undefined ? params.groupby : params.groupBy),
      orderby: buildOrderBy(params.orderby !== undefined ? params.orderby : params.orderBy),
      limit: Math.min(Math.max(Number(params.pageSize || params.limit) || 100, 1), 1000),
      offset: Number(params.offset) || 0,
      withsubrecords: false,
      paramrecord: null,
      entityids: [],
    },
  }).then(result => {
    const qry = result.query || {}
    return {
      records: qry.recordset ? qry.recordset.records : [],
      recordcount: qry.recordcount !== undefined ? qry.recordcount : null,
    }
  })
}

export function saveObjectRecord(objectsid, record) {
  const rec = normalizeRecord(record)
  // saverecord требует ячейку id даже при вставке (штатные формы получают её
  // из loadNewRecord); пустой id = insert, заполненный = update
  const id = 'id' in record ? normalizeRecordId(record.id) : null
  rec.id = { value: id, title: null, status: 'changed' }
  return runQuery({
    method: 'saverecord',
    mode: 'sync',
    param: {
      keymode: 'sid',
      objectsid,
      objectid: null,
      record: rec,
    },
  }).then(result => ({ record: result.record || null }))
}

// Insert-or-update по unique-ключам. Метода upsert у бэка нет — операция
// собирается из query + saverecord, как это делает cubus_upsert_records.
// Расхождение с ним осознанное: несколько совпадений — ошибка, а не «обновим
// первое попавшееся» (PROP-001: тихо неверный результат хуже отказа).
export function upsertObjectRecord(objectsid, record, uniqueKeys) {
  if (!record || typeof record !== 'object') {
    throw bridgeError('E_PROTOCOL', 'record must be an object')
  }
  const keys = Array.isArray(uniqueKeys) ? uniqueKeys.filter(k => k) : []
  if (keys.length === 0) {
    throw bridgeError('E_PROTOCOL', 'uniqueKeys must be a non-empty array of attribute sids')
  }
  const filter = {}
  keys.forEach(key => {
    if (!(key in record)) {
      throw bridgeError('E_PROTOCOL', `unique key "${key}" is missing from the record`)
    }
    const v = rawValue(record[key])
    if (v === null || v === undefined || v === '') {
      throw bridgeError('E_PROTOCOL', `unique key "${key}" must have a value`)
    }
    filter[key] = v
  })
  return queryObject({ objectsid, filter, limit: 10 }).then(res => {
    // Удалённая строка не должна ни воскресать под видом update, ни давать ложный
    // конфликт. С PROP-046 их отсекает уже сам запрос (hidedeleted), но проверка
    // остаётся: фронт и бэк выкатываются раздельно, и на старом бэке страница не
    // должна начать воскрешать удалённые записи молча.
    const found = (res.records || []).filter(r => rawValue(r.del) !== true)
    if (found.length > 1) {
      throw bridgeError('E_CONFLICT', `unique keys match ${found.length} records of "${objectsid}"`, { matched: found.length })
    }
    if (found.length === 0) {
      return saveObjectRecord(objectsid, record).then(r => ({ record: r.record, action: 'inserted' }))
    }
    const id = rawValue(found[0].id)
    if (id === null || id === undefined || id === '') {
      throw bridgeError('E_QUERY', 'matched record has no id')
    }
    // Update шлёт только переданные поля + id: отсутствующее required-поле бэк
    // требует лишь у НОВОЙ записи, а пустое значение со status changed отвергает —
    // поэтому копировать найденную запись целиком не нужно и вредно.
    const upd = { ...record, id }
    return saveObjectRecord(objectsid, upd).then(r => ({ record: r.record, action: 'updated' }))
  })
}

export function deleteObjectRecord(objectsid, record) {
  return runQuery({
    method: 'deleterecord',
    mode: 'sync',
    param: {
      objectid: null,
      objectsid,
      record: normalizeRecord(record),
    },
  }).then(result => ({ record: result.record || null }))
}

export function runProcedure(procsid, params) {
  return runQuery({
    method: 'runprocedure',
    mode: 'sync',
    param: {
      keymode: 'sid',
      objectid: null,
      objectsid: procsid,
      record: normalizeRecord(params || {}),
    },
  })
}

function cell(record, sid) {
  return record && record[sid] ? record[sid].value : null
}

function parseList(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

// Загрузка записи htmlpage по sid → { sid, name, html, objectwhitelist, procwhitelist }
export function loadHtmlPage(pagesid) {
  if (!pagesid) return Promise.reject(bridgeError('E_INTERNAL', 'htmlpage sid is not set'))
  return queryObject({
    objectsid: 'htmlpage',
    filter: { sid: pagesid, del: false },
    limit: 1,
  }).then(res => {
    const rec = res.records && res.records[0]
    if (!rec) throw bridgeError('E_INTERNAL', `htmlpage "${pagesid}" not found`)
    return {
      sid: cell(rec, 'sid'),
      name: cell(rec, 'name'),
      html: cell(rec, 'html') || '',
      objectwhitelist: parseList(cell(rec, 'objectwhitelist')),
      procwhitelist: parseList(cell(rec, 'procwhitelist')),
      record: rec,
    }
  })
}
